import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { ProfileProvider, useProfileState } from '@/contexts/ProfileContext';
import Welcome from '@/pages/Welcome';
import AuthCallback from '@/pages/AuthCallback';
import ResetPassword from '@/pages/ResetPassword';
import Step0Connect from '@/pages/Onboarding/Step0Connect';
import Step1Confirm from '@/pages/Onboarding/Step1Confirm';
import Step2Pivot from '@/pages/Onboarding/Step2Pivot';
import Step3Review from '@/pages/Onboarding/Step3Review';
import AnalysisLoading from '@/pages/AnalysisLoading';
import DashboardLayout from '@/pages/Dashboard/DashboardLayout';
import Overview from '@/pages/Dashboard/Overview';
import SkillsProfile from '@/pages/Dashboard/SkillsProfile';
import RoleExplorer from '@/pages/Dashboard/RoleExplorer';
import JDAnalyzer from '@/pages/Dashboard/JDAnalyzer';
import MyProgress from '@/pages/Dashboard/MyProgress';
import NetworkIn from '@/pages/Dashboard/NetworkIn';
import PrintReport from '@/pages/Dashboard/PrintReport';
import JDPrintReport from '@/pages/Dashboard/JDPrintReport';
import { Spinner } from '@/components/atoms';
import { Toaster } from 'sonner';
import '@/App.css';

function FullPageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-sd-ivory">
      <Spinner size={40} />
    </div>
  );
}

function Gate() {
  const { user, loading } = useAuth();
  const { savedProfile, analysis, loading: profileLoading } = useProfileState();
  const navigate = useNavigate();
  const location = useLocation();

  // Side-effects (route changes) MUST happen in useEffect, not during render
  React.useEffect(() => {
    if (loading || profileLoading) return;
    if (!user) return; // Unauthenticated redirect is handled below via <Navigate /> in render
    if (location.pathname !== '/') return; // Only auto-route from the welcome path
    if (analysis) navigate('/dashboard', { replace: true });
    else if (savedProfile?.pivot_type) navigate('/onboarding/review', { replace: true });
    else if (savedProfile?.name) navigate('/onboarding/confirm', { replace: true });
    else navigate('/onboarding/connect', { replace: true });
  }, [loading, profileLoading, user, analysis, savedProfile, location.pathname, navigate]);

  if (loading || profileLoading) return <FullPageLoader />;
  if (!user && location.pathname !== '/' && !location.pathname.startsWith('/auth/')) {
    return <Navigate to="/" replace />;
  }
  return null;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ProfileProvider>
            <Gate />
            <Routes>
              <Route path="/" element={<Welcome />} />
              <Route path="/auth/linkedin/callback" element={<AuthCallback />} />
              <Route path="/auth/reset-password" element={<ResetPassword />} />
              <Route path="/onboarding/connect" element={<Step0Connect />} />
              <Route path="/onboarding/confirm" element={<Step1Confirm />} />
              <Route path="/onboarding/pivot" element={<Step2Pivot />} />
              <Route path="/onboarding/review" element={<Step3Review />} />
              <Route path="/analysis/loading" element={<AnalysisLoading />} />
              {/* Full-profile export report — no sidebar, print-optimised */}
              <Route path="/dashboard/report" element={<PrintReport />} />
              {/* Per-JD export report — no sidebar, print-optimised */}
              <Route path="/dashboard/jd-report/:id" element={<JDPrintReport />} />
              <Route path="/dashboard" element={<DashboardLayout />}>
                <Route index element={<Overview />} />
                <Route path="skills" element={<SkillsProfile />} />
                <Route path="roles" element={<RoleExplorer />} />
                <Route path="jd" element={<JDAnalyzer />} />
                <Route path="network" element={<NetworkIn />} />
                <Route path="progress" element={<MyProgress />} />
                {/* Legacy redirects for any cached links */}
                <Route path="gaps" element={<Navigate to="/dashboard/skills" replace />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            {/* Global toast portal — dark, blurred, per design guidelines */}
            <Toaster
              position="bottom-right"
              theme="dark"
              richColors={false}
              closeButton
              toastOptions={{
                style: {
                  background: 'rgba(10, 10, 10, 0.85)',
                  color: '#FFFFFF',
                  border: '1px solid rgba(76, 217, 100, 0.25)',
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                },
                classNames: {
                  title: 'text-white font-semibold',
                  description: 'text-[#B8C0B8]',
                },
              }}
            />
          </ProfileProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
